import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import { useAccess } from '@umijs/max';
import { Alert, App, Button, Drawer, Empty, Pagination, Progress, Space, Table, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  downloadRequestKey, downloadSavedArtifact, downloadTasksApi, isDownloadActive,
  isDownloadAvailable, legacyDownloadFileTypes, notifyDownloadCreated,
} from '@/services/downloadTasks';
import type { DownloadLimits, DownloadTask, DownloadTaskItem } from '@/services/downloadTasks';
import DownloadFileDetails from './DownloadFileDetails';
import { downloadFileSize, downloadStatusColor, statusLabels } from './downloadFileTree';

// 保持原公开格式化函数兼容，任务列表和文件明细共用同一份单位规则。
export { downloadFileSize } from './downloadFileTree';

/** 展示服务端时刻，重复下载不会修改完成时间或有效期。 */
function time(value?: string): string { return value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '—'; }

/** 顶部统一下载中心，抽屉打开且当前有后台任务时轮询，领取只请求已有ZIP。 */
export default function DownloadCenter() {
  const access = useAccess();
  const { message } = App.useApp();
  const [open, setOpen] = useState(false);
  const [tasks, setTasks] = useState<DownloadTask[]>([]);
  const [limits, setLimits] = useState<DownloadLimits>();
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [activeCount, setActiveCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [detail, setDetail] = useState<DownloadTask>();
  const [items, setItems] = useState<DownloadTaskItem[]>([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemsError, setItemsError] = useState('');
  const [detailReload, setDetailReload] = useState(0);
  const [busyId, setBusyId] = useState<string>();
  const retryKeys = useRef(new Map<string, string>());
  const generation = useRef(0);
  const canAccess = access.canAccessReportReview || access.canAccessReportStatistics;
  const currentDetail = tasks.find(task => String(task.id) === String(detail?.id)) || detail;
  const detailId = detail?.id;
  // 历史任务与新增附件共用服务端类型名称，旧服务只回退原有三种报告。
  const formatLabels = Object.fromEntries((limits?.fileTypes ?? legacyDownloadFileTypes)
    .map(type => [type.value, type.label]));

  /** 加载本人任务，旧请求晚返回时不得覆盖关闭后或另一页的新结果。 */
  const load = useCallback(async () => {
    const current = ++generation.current;
    setLoading(true);
    try {
      const result = await downloadTasksApi.list(page);
      if (current !== generation.current) return;
      setTasks(result.list || []); setTotal(Number(result.total || 0)); setActiveCount(Number(result.activeCount || 0)); setError('');
    } catch { if (current === generation.current) setError('下载任务加载失败，请刷新重试'); }
    finally { if (current === generation.current) setLoading(false); }
  }, [page]);

  useEffect(() => {
    if (!open || !canAccess) return;
    void load();
    downloadTasksApi.limits().then(setLimits).catch(() => setError('下载规则加载失败，请刷新重试'));
    return () => { generation.current++; };
  }, [open, canAccess, load]);

  useEffect(() => {
    if (!open || !activeCount) return;
    const timer = window.setInterval(() => { void load(); }, 5000);
    return () => window.clearInterval(timer);
  }, [open, activeCount, load]);

  useEffect(() => {
    /** 用户点击业务页消息中的入口时打开抽屉，不触发下载。 */
    const show = () => { if (canAccess) { setPage(1); setOpen(true); } };
    /** 新任务创建时只刷新已打开的抽屉，不打断当前业务页。 */
    const refresh = () => { if (open) { setPage(1); void load(); } };
    window.addEventListener('download-center-open', show);
    window.addEventListener('download-center-changed', refresh);
    return () => {
      window.removeEventListener('download-center-open', show);
      window.removeEventListener('download-center-changed', refresh);
    };
  }, [canAccess, open, load]);

  useEffect(() => {
    if (!detailId) return;
    let current = true;
    setItemsLoading(true);
    setItemsError('');
    /** 完整读取冻结清单再构造目录；只读元数据，失败页不得伪装成完整目录。 */
    async function loadItems(): Promise<void> {
      try {
        const first = await downloadTasksApi.items(String(detailId), 1, 100);
        if (!current) return;
        const totalItems = Number(first.total);
        if (!Number.isSafeInteger(totalItems) || totalItems < 0) throw new Error('无效的文件清单数量');
        const complete = [...(first.list || [])];
        for (let next = 2; next <= Math.ceil(totalItems / 100); next++) {
          const result = await downloadTasksApi.items(String(detailId), next, 100);
          if (!current) return;
          complete.push(...(result.list || []));
        }
        if (complete.length !== totalItems) throw new Error('文件清单数量已变化');
        if (current) setItems(complete);
      } catch {
        if (current) { setItems([]); setItemsError('文件明细加载失败，请重新加载'); }
      } finally { if (current) setItemsLoading(false); }
    }
    void loadItems();
    return () => { current = false; };
  }, [detailId, currentDetail?.status, currentDetail?.processedCount, detailReload]);

  /** 先刷新当前状态，再让浏览器原生传输已有文件；不创建下载任务。 */
  async function download(task: DownloadTask): Promise<void> {
    setBusyId(String(task.id));
    try {
      const current = await downloadTasksApi.detail(String(task.id));
      if (!isDownloadAvailable(current.status)) { message.warning('文件已清理或尚未就绪，请刷新任务'); await load(); return; }
      downloadSavedArtifact(String(task.id));
      message.info('已请求下载保存的文件，无需重新打包');
    } catch { /* 统一请求层展示鉴权和请求失败。 */ }
    finally { setBusyId(undefined); }
  }

  /** 用户明确要求最新报告才创建新任务；网络失败时保留UUID以确保幂等。 */
  async function retry(task: DownloadTask): Promise<void> {
    const id = String(task.id);
    const key = retryKeys.current.get(id) || downloadRequestKey();
    retryKeys.current.set(id, key);
    setBusyId(id);
    try {
      await downloadTasksApi.retry(id, key);
      retryKeys.current.delete(id);
      message.success('已创建重新打包任务，原文件的保留时间不变');
      setPage(1); notifyDownloadCreated(); await load();
    } catch { /* 统一请求层展示错误，保留键供网络重试。 */ }
    finally { setBusyId(undefined); }
  }

  if (!canAccess) return null;
  return <>
    <Button type="text" icon={<DownloadOutlined />} onClick={() => { setPage(1); setOpen(true); }}>下载中心</Button>
    <Drawer title="下载中心" open={open} width="min(1140px, 100vw)" onClose={() => { setOpen(false); setDetail(undefined); }}
      extra={<Button icon={<ReloadOutlined />} loading={loading} onClick={() => { void load(); }}>刷新</Button>}>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Alert type="info" showIcon message={limits
          ? `每人保留最近 ${limits.retainedCount} 次下载产物，最长 ${limits.retentionDays} 天`
          : '重复下载直接领取已保存文件'}
          description="产物为打包时的资料快照，每个任务单独一个文件夹。重复下载不重新生成、不重新打包、不延长保留时间；需要最新资料请重新打包。" />
        {error && <Alert type="error" showIcon message={error} />}
        <Table<DownloadTask> rowKey={task => String(task.id)} dataSource={tasks} loading={loading} pagination={false}
          locale={{ emptyText: <Empty description="暂无下载任务，可在我的审核或报告统计中批量选择报告" /> }}
          scroll={{ x: 1000 }} columns={[
            { title: '下载任务', key: 'name', width: 190, render: (_, task) => <Space direction="vertical" size={0}>
              <Typography.Text>{task.source === 'REPORT_REVIEW' ? '我的审核' : '报告统计'}批量报告</Typography.Text>
              <Typography.Text type="secondary">{task.taskCount} 个任务 / {task.status === 'PENDING' ? '等待展开文件清单' : `${task.totalCount} 份文件`}</Typography.Text>
              <Typography.Text type="secondary">{time(task.createTime)}</Typography.Text>
            </Space> },
            { title: '状态与进度', key: 'progress', width: 210, render: (_, task) => <Space direction="vertical" size={0} style={{ width: '100%' }}>
              <Tag color={downloadStatusColor(task.status)}>
                {statusLabels[task.status] || task.status}
              </Tag>
              {isDownloadActive(task.status) && <Progress size="small"
                percent={task.totalCount ? Math.floor(task.processedCount / task.totalCount * 100) : 0} />}
              <Typography.Text type="secondary">成功 {task.successCount} / 失败 {task.failureCount}</Typography.Text>
              {task.errorMessage && <Typography.Text type="danger">{task.errorMessage}</Typography.Text>}
              {task.cleanupReason && <Typography.Text type="secondary">
                {task.cleanupReason === 'EXPIRED_BY_COUNT' ? '超过最近保留数量' : '超过文件有效期'}，可重新打包
              </Typography.Text>}
            </Space> },
            { title: '文件大小', key: 'size', width: 110, render: (_, task) => downloadFileSize(task.fileSize) },
            { title: '完成 / 到期', key: 'times', width: 170, render: (_, task) => <Space direction="vertical" size={0}>
              <span>{time(task.finishedTime)}</span><Typography.Text type="secondary">{time(task.expireTime)}</Typography.Text>
            </Space> },
            { title: '操作', key: 'actions', width: 230, render: (_, task) => <Space wrap>
              <Button type="link" size="small" onClick={() => { setItems([]); setItemsError(''); setDetail(task); }}>查看明细</Button>
              {isDownloadAvailable(task.status) && <Button type="link" size="small" loading={busyId === String(task.id)}
                onClick={() => { void download(task); }}>下载文件</Button>}
              {!isDownloadActive(task.status) && <Button type="link" size="small" loading={busyId === String(task.id)}
                onClick={() => { void retry(task); }}>重新打包最新报告</Button>}
            </Space> },
          ]} />
        <Pagination current={page} pageSize={10} total={total} showSizeChanger={false} onChange={setPage} />
      </Space>
      <Drawer title="下载文件明细" width="min(1200px, 100vw)" open={!!detail} onClose={() => setDetail(undefined)}
        styles={{ body: { padding: 0 } }}
        extra={<Button icon={<ReloadOutlined />} loading={itemsLoading} onClick={() => setDetailReload(value => value + 1)}>刷新</Button>}>
        {currentDetail && <DownloadFileDetails key={currentDetail.id} task={currentDetail} items={items} labels={formatLabels}
          loading={itemsLoading} error={itemsError} onReload={() => setDetailReload(value => value + 1)} />}
      </Drawer>
    </Drawer>
  </>;
}
